import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["baseFilterFamily", "editButtonsState", "layout", "onEditModeChange", "onUserAction", "onViewerRender", "uiMessages"];
import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { getLogicGroupConditionsSize, isLogicGroup } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import { isLogicGroupValid } from '../filterQueryFormat/logic/LogicGroup';
import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { getUniqueFieldIdsByAssetFamily } from '../filterQueryFormat/FilterQueryFormat';
import { isFilterValueHasRestrictedPropertiesUsingRestrictedValueCheck } from 'customer-data-filters-ui/utils/fieldLevelPermissions';
import { useInternalValue } from '../hooks/useInternalValue';
import { EDITED_FILTERS } from '../constants/EditModeActionTypes';
import { initialState, SegmentationFiltersInstanceReducer } from './SegmentationFiltersInstanceReducer';
import SegmentationFiltersSharedContext from './SegmentationFiltersSharedContext';
import { useQuickFiltersInternalValue } from '../hooks/useQuickFiltersInternalValue';
import emptyFunction from 'react-utils/emptyFunction';
import { hasResetFilterValueLogic } from '../permissions/gates';
import { useSegmentationFiltersInstanceContextPerformanceTracking } from '../hooks/useSegmentationFiltersInstanceContextPerformanceTracking';
import { useRenderPropertiesTracked } from '../hooks/useRenderPropertiesTracked';
import { jsx as _jsx } from "react/jsx-runtime";
const SegmentationFiltersInstanceContext = /*#__PURE__*/createContext({});
const defaultEditButtonState = {
  disabled: false
};
const defaultUIMessages = {};
const SegmentationFiltersInstanceProvider = _ref => {
  let {
      baseFilterFamily = DSAssetFamilies.CONTACT_PROPERTY,
      editButtonsState = defaultEditButtonState,
      layout = 'panel',
      onEditModeChange = emptyFunction,
      onUserAction = emptyFunction,
      onViewerRender = emptyFunction,
      uiMessages = defaultUIMessages
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const props = useMemo(() => Object.assign({
    baseFilterFamily,
    editButtonsState,
    layout,
    onEditModeChange,
    onUserAction,
    onViewerRender,
    uiMessages
  }, rest), [baseFilterFamily, editButtonsState, layout, onEditModeChange, onUserAction, onViewerRender, rest, uiMessages]);
  const {
    isReadOnly,
    openInEditMode,
    originalValue,
    skipEmptyFiltersState,
    revision,
    value,
    quickFilters,
    children
  } = props;
  const [state, dispatch] = useReducer(SegmentationFiltersInstanceReducer, Object.assign({}, initialState));
  const dispatchTracked = useRenderPropertiesTracked(dispatch);
  const {
    onEditModeChangeTracked
  } = useSegmentationFiltersInstanceContextPerformanceTracking(state.isLoadingViewerData, onEditModeChange);
  const {
    currencyCode,
    data,
    fetchMore,
    getIsUngated,
    getTrackerInstance,
    hasLimit,
    hasScope,
    isUserInfoFetched,
    portalId,
    userId,
    props: sharedProps
  } = useContext(SegmentationFiltersSharedContext);
  const filtersContainerRef = useRef(null);
  const internalValue = useInternalValue(baseFilterFamily, sharedProps.strategy, value, data, fetchMore, getIsUngated, hasScope, isUserInfoFetched);
  const quickFiltersInternalValue = useQuickFiltersInternalValue(baseFilterFamily, sharedProps.strategy, quickFilters, data, fetchMore, getIsUngated, hasScope, isUserInfoFetched);
  const prevInternalValueRef = useRef(internalValue);
  useEffect(() => {
    if (
    // do not update when filters data is still loading
    state.isLoadingViewerData ||
    // do not update if editor is open
    !hasResetFilterValueLogic() && state.draftCondition) {
      return;
    }
    const canUpdateExistingInternalDraftValue =
    // do not update if `internalDraftValue` is not valid
    isLogicGroup(state.internalDraftValue) && isLogicGroupValid(state.internalDraftValue, getIsUngated) &&
    // do not update if new incoming value is the same as previous one
    isLogicGroup(internalValue) && !internalValue.equals(prevInternalValueRef.current) && !state.draftCondition;
    if (!state.internalDraftValue || canUpdateExistingInternalDraftValue) {
      dispatch({
        type: 'internalDraftValue',
        payload: internalValue
      });
    }
    prevInternalValueRef.current = internalValue;
  }, [internalValue, state.internalDraftValue, state.isLoadingViewerData, state.draftCondition, getIsUngated, baseFilterFamily]);
  const requiredIdsByAssetFamily = useMemo(() => {
    const requiredIdsByAssetFamilyMain = getUniqueFieldIdsByAssetFamily(internalValue);
    const requiredIdsByAssetFamilyQuickFilters = getUniqueFieldIdsByAssetFamily(quickFiltersInternalValue);
    return requiredIdsByAssetFamilyMain.mergeDeep(requiredIdsByAssetFamilyQuickFilters);
  }, [internalValue, quickFiltersInternalValue]);
  const prevRequiredIdsByAssetFamily = useRef(undefined);
  useEffect(() => {
    if (!isUserInfoFetched || isFilterValueHasRestrictedPropertiesUsingRestrictedValueCheck(value) || requiredIdsByAssetFamily.equals(prevRequiredIdsByAssetFamily.current)) {
      return;
    }
    prevRequiredIdsByAssetFamily.current = requiredIdsByAssetFamily;
    dispatch({
      type: 'requiredIdsByAssetFamily',
      payload: requiredIdsByAssetFamily
    });
    const onMountProps = {
      baseFilterFamily,
      fetchMore,
      getIsUngated,
      hasScope
    };
    sharedProps.strategy.onMount(onMountProps, requiredIdsByAssetFamily).then(() => {
      dispatch({
        type: 'loadingViewerData',
        payload: false
      });
    }).catch(() => {
      // disable loader even if some data failed to fetch
      dispatch({
        type: 'loadingViewerData',
        payload: false
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserInfoFetched, baseFilterFamily, requiredIdsByAssetFamily]);

  // imitation of `componentDidUpdate(prevProps)`
  const prevData = useRef(data);
  useEffect(() => {
    const onMountPrevProps = {
      data: prevData.current,
      fetchMore
    };
    const onMountNewProps = {
      data,
      fetchMore
    };
    // @ts-expect-error ts-migrate(2322) FIXME
    sharedProps.strategy.onUpdate(onMountPrevProps, onMountNewProps);
    prevData.current = data;
  }, [data, fetchMore, sharedProps.strategy]);
  const prevIsInEditMode = useRef(state.isInEditMode);
  const prevIsReadOnly = useRef(isReadOnly);
  const prevRevision = useRef(revision);
  const prevBaseFilterFamily = useRef(baseFilterFamily);
  const filterValueHasBeenReset = hasResetFilterValueLogic() && isLogicGroup(internalValue) && prevInternalValueRef.current && !internalValue.equals(prevInternalValueRef.current) && getLogicGroupConditionsSize(internalValue) === 0;
  useEffect(() => {
    if (isReadOnly || prevRevision.current !== revision || prevBaseFilterFamily.current !== baseFilterFamily || filterValueHasBeenReset) {
      dispatch({
        type: 'resetEditor',
        payload: {
          internalDraftValue: internalValue,
          shouldLeaveEditorOpen: openInEditMode
        }
      });
    }

    // reset filters when switching from edit to view mode
    if (prevIsInEditMode.current && !state.isInEditMode || !prevIsReadOnly.current && isReadOnly) {
      dispatch({
        type: 'externalOriginalValue',
        payload: undefined
      });
    }

    // reset externalOriginalValue when switching from view to edit mode
    if (!prevIsInEditMode.current && state.isInEditMode) {
      dispatch({
        type: 'externalOriginalValue',
        payload: originalValue || value
      });
    }
    prevIsInEditMode.current = state.isInEditMode;
    prevIsReadOnly.current = isReadOnly;
    prevRevision.current = revision;
    prevBaseFilterFamily.current = baseFilterFamily;
  }, [isReadOnly, internalValue, state.isInEditMode, state.internalDraftValue, originalValue, revision, value, baseFilterFamily, filterValueHasBeenReset, openInEditMode]);
  const hasSkippedEmptyFiltersState = useRef(false);
  const shouldSkipEmptyFiltersState = skipEmptyFiltersState && !hasSkippedEmptyFiltersState.current && state.internalDraftValue && getLogicGroupConditionsSize(state.internalDraftValue) === 0;
  useEffect(() => {
    if (shouldSkipEmptyFiltersState) {
      dispatch({
        type: 'addFilterGroup',
        payload: baseFilterFamily
      });
      onEditModeChangeTracked(EDITED_FILTERS);
      hasSkippedEmptyFiltersState.current = true;
    }
  }, [baseFilterFamily, onEditModeChangeTracked, shouldSkipEmptyFiltersState]);
  const hasOpenedInEditMode = useRef(false);
  const shouldOpenInEditMode = openInEditMode && !hasOpenedInEditMode.current && !state.isInEditMode && state.internalDraftValue;
  useEffect(() => {
    if (!shouldOpenInEditMode) {
      return;
    }
    if (!shouldSkipEmptyFiltersState) {
      dispatch({
        type: 'editFilters'
      });
      onEditModeChangeTracked(EDITED_FILTERS);
    }
    hasOpenedInEditMode.current = true;
  }, [onEditModeChangeTracked, shouldOpenInEditMode, shouldSkipEmptyFiltersState]);
  const contextProps = useMemo(() => Object.assign({}, props, {
    onEditModeChange: onEditModeChangeTracked
  }, sharedProps, {
    showAllFiltersInEditMode: props.useOnePanel && props.showAllFiltersInEditMode
  }), [props, sharedProps, onEditModeChangeTracked]);
  const contextValue = useMemo(() => Object.assign({}, state, {
    quickFiltersInternalValue,
    currencyCode,
    data,
    dispatch: dispatchTracked,
    fetchMore,
    filtersContainerRef,
    getIsUngated,
    getTrackerInstance,
    hasLimit,
    hasScope,
    isUserInfoFetched,
    portalId,
    props: contextProps,
    userId
  }), [state, quickFiltersInternalValue, currencyCode, data, dispatchTracked, fetchMore, filtersContainerRef, getIsUngated, getTrackerInstance, hasLimit, hasScope, isUserInfoFetched, portalId, contextProps, userId]);
  return /*#__PURE__*/_jsx(SegmentationFiltersInstanceContext.Provider, {
    value: contextValue,
    children: children
  });
};
export { SegmentationFiltersInstanceProvider };
export default SegmentationFiltersInstanceContext;